import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bell, Package, Truck, RotateCcw, Receipt, Wallet, LifeBuoy, ShieldCheck,
  BadgePercent, Megaphone, Star, CheckCheck, SlidersHorizontal, BellRing,
} from "lucide-react";
import api from "@/lib/api";
import { PageHeader } from "@/components/ui-helpers";
import EmptyState from "@/components/EmptyState";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { enablePush, disablePush, getPushStatus, pushSupported } from "@/lib/push";

const KIND_ICON = {
  order: Package, shipping: Truck, return: RotateCcw, refund: Receipt, payment: Wallet,
  settlement: Wallet, support: LifeBuoy, seller_verification: ShieldCheck,
  promotion: BadgePercent, marketing: Megaphone, review: Star,
};
const LEVEL = {
  urgent: { dot: "bg-rose-500", tint: "bg-rose-50 text-rose-500" },
  warning: { dot: "bg-amber-500", tint: "bg-amber-50 text-amber-500" },
  success: { dot: "bg-emerald-500", tint: "bg-emerald-50 text-emerald-500" },
  info: { dot: "bg-blue-500", tint: "bg-blue-50 text-blue-500" },
};
const KIND_LABEL = {
  all: "All", order: "Orders", shipping: "Shipping", return: "Returns", refund: "Refunds",
  payment: "Payments", settlement: "Settlements", support: "Support",
  seller_verification: "Verification", promotion: "Promotions", marketing: "Announcements",
};
const PREF_LABELS = {
  inapp_orders: "Order & payment alerts", inapp_support: "Support updates",
  inapp_seller: "Seller & settlement alerts", inapp_promotions: "Promotion alerts",
  inapp_marketing: "Marketing announcements (in-app)", email_marketing: "Marketing emails",
  whatsapp_marketing: "WhatsApp marketing messages",
};

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function NotificationsPage() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  const [prefs, setPrefs] = useState(null);
  const [showPrefs, setShowPrefs] = useState(false);
  const navigate = useNavigate();
  const [push, setPush] = useState({ supported: false, subscribed: false, permission: "default" });
  const [pushBusy, setPushBusy] = useState(false);
  useEffect(() => { getPushStatus().then(setPush).catch(() => {}); }, []);
  const togglePush = async () => {
    setPushBusy(true);
    try {
      if (push.subscribed) { await disablePush(); toast.success("Push notifications turned off"); }
      else { await enablePush(); toast.success("Push on — we'll alert you even when the app is closed"); }
      setPush(await getPushStatus());
    } catch (e) { toast.error(e.message || "Couldn't update notifications"); }
    finally { setPushBusy(false); }
  };

  const load = useCallback((kind) => {
    const k = kind || filter;
    api.get(`/notifications/inbox${k !== "all" ? `?kind=${k}` : ""}`)
      .then((r) => setData(r.data)).catch(() => setData({ items: [], unread: 0, groups: {} }));
  }, [filter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/notifications/settings").then((r) => setPrefs(r.data.preferences)).catch(() => {}); }, []);

  const items = data?.items || [];
  const groups = data?.groups || {};

  const open = async (n) => {
    if (!n.read) { try { await api.post("/notifications/mark-read", { id: n.id }); } catch (e) {} }
    if (n.route) navigate(n.route);
  };
  const readAll = async () => {
    try { await api.post("/notifications/mark-all-read"); toast.success("All caught up"); load(); }
    catch (e) { toast.error("Could not mark all as read"); }
  };
  const togglePref = async (key) => {
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next);
    try { await api.put("/notifications/settings", { preferences: next }); }
    catch (e) { toast.error("Could not save preference"); }
  };

  const chips = ["all", ...Object.keys(groups).filter((k) => k !== "all")];

  return (
    <div className="mx-auto max-w-2xl">
      <PageHeader title="Notifications" icon={Bell}
        subtitle="Every order, payment, shipping, return, support and announcement update — one central center."
        actions={<div className="flex items-center gap-2">
          <button onClick={() => setShowPrefs((s) => !s)} data-testid="notif-prefs-toggle" className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-600 hover:border-blue-300"><SlidersHorizontal className="h-4 w-4" /> Preferences</button>
          {items.some((n) => !n.read) && (
            <button onClick={readAll} data-testid="notif-read-all" className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-600 hover:border-emerald-300 hover:text-emerald-600"><CheckCheck className="h-4 w-4" /> Mark all read</button>
          )}
        </div>} />

      {showPrefs && prefs && (
        <div className="card-soft mb-4 p-4" data-testid="notif-prefs-panel">
          {pushSupported() && (
            <div className="mb-3 flex items-center justify-between gap-3 rounded-lg border border-blue-100 bg-blue-50/60 px-3 py-2.5">
              <div className="flex items-start gap-2">
                <BellRing className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
                <span className="text-sm text-slate-600"><span className="font-semibold text-slate-700">Push notifications</span><br /><span className="text-xs text-slate-400">Ride, order &amp; payment alerts reach you even when the app is closed or your screen is off.</span></span>
              </div>
              <button onClick={togglePush} disabled={pushBusy} data-testid="notif-push-toggle" className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold disabled:opacity-50 ${push.subscribed ? "border border-slate-200 bg-white text-slate-600 hover:border-rose-300 hover:text-rose-600" : "bg-blue-600 text-white hover:bg-blue-700"}`}>{pushBusy ? "…" : push.subscribed ? "Turn off" : "Enable"}</button>
            </div>
          )}
          <p className="mb-2 text-sm font-semibold text-slate-700">Which updates reach your bell &amp; inbox?</p>
          <div className="grid gap-2 sm:grid-cols-2">
            {Object.keys(PREF_LABELS).filter((k) => k in prefs).map((k) => (
              <label key={k} className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2 text-sm text-slate-600">
                {PREF_LABELS[k]}
                <Switch checked={!!prefs[k]} onCheckedChange={() => togglePref(k)} data-testid={`pref-${k}`} />
              </label>
            ))}
          </div>
          <p className="mt-3 text-xs text-slate-400">Order, payment and support alerts are always delivered for your account's security. Marketing emails are only sent with your consent.</p>
          {"personalization" in prefs && (
            <div className="mt-4 border-t border-slate-100 pt-4">
              <label className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2.5 text-sm text-slate-600">
                <span><span className="font-semibold text-slate-700">Personalized recommendations</span><br /><span className="text-xs text-slate-400">Use your views, wishlist and purchases to tailor "Recommended for you". Turn off for non-personalized suggestions.</span></span>
                <Switch checked={!!prefs.personalization} onCheckedChange={() => togglePref("personalization")} data-testid="pref-personalization" />
              </label>
            </div>
          )}
        </div>
      )}

      {chips.length > 1 && (
        <div className="mb-4 flex flex-wrap gap-2" data-testid="notif-filter-chips">
          {chips.map((k) => (
            <button key={k} onClick={() => { setFilter(k); setData(null); load(k); }} data-testid={`notif-chip-${k}`}
              className={`rounded-full px-3.5 py-1.5 text-xs font-medium transition ${filter === k ? "bg-slate-900 text-white" : "border border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}>
              {KIND_LABEL[k] || k}{k !== "all" && groups[k] ? ` (${groups[k]})` : ""}
            </button>
          ))}
        </div>
      )}

      {data === null ? (
        <div className="space-y-3">{[0, 1, 2].map((i) => <div key={i} className="h-16 animate-pulse rounded-xl bg-white/70" />)}</div>
      ) : items.length === 0 ? (
        <EmptyState icon={Bell} title="You're all caught up" description="Updates about your orders, payments, shipping, returns, support and announcements will appear here." testid="empty-notifications" />
      ) : (
        <div className="space-y-3">
          {items.map((n) => {
            const lv = LEVEL[n.level] || LEVEL.info;
            const TypeIcon = KIND_ICON[n.kind] || Bell;
            return (
              <button key={n.id} onClick={() => open(n)} data-testid="notification-item"
                className={`card-soft flex w-full items-start gap-3 p-4 text-left transition hover:border-blue-200 ${n.read ? "opacity-60" : ""}`}>
                <span className={`relative mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${lv.tint}`}>
                  <TypeIcon className="h-4 w-4" />
                  {!n.read && <span className={`absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full ${lv.dot} ring-2 ring-white`} />}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-slate-800">{n.title}</p>
                  {n.subtitle && <p className="text-sm text-slate-500">{n.subtitle}</p>}
                  <p className="mt-0.5 text-[11px] text-slate-400">{timeAgo(n.created_at)}</p>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
